import { format, isValid, parseISO } from 'date-fns';

import { formatValue } from 'react-currency-input-field';

import { HStack, Table, TableContainer, Tbody, Td, Text, Th, Thead, Tr } from '@chakra-ui/react';

import ProfileField from 'app/components/UI/ProfileField';
import ProfileSection from 'app/components/UI/ProfileSection';

import useFetchAllPayCycles from 'app/hooks/useFetchAllPayCycles';
import useFetchPayCalendar from 'app/hooks/useFetchPayCalendar';

import { derivePaySchedule } from 'app/utils/payCalendars';
import { getPayCalendarProfilePath } from 'app/utils/routes';

import { DEFAULT_DATE_FORMAT } from '@aslanpay/portal-common-lib';

import { IndividualPayrollTabViewProps } from './types';

/**
 * Formats an ISO date string for display, returning undefined for missing or unparseable values so
 * a single bad/null date on a record can't crash the whole tab (date-fns' format throws on an
 * invalid date).
 */
const formatDate = (date?: string) => {
  if (!date) {
    return undefined;
  }
  const parsed = parseISO(date);
  return isValid(parsed) ? format(parsed, DEFAULT_DATE_FORMAT) : undefined;
};

function IndividualPayrollTabView({ individual }: IndividualPayrollTabViewProps) {
  const { data: payCalendar } = useFetchPayCalendar(individual?.payCalendarId);
  const { data: payCycles } = useFetchAllPayCycles(individual?.payCalendarId);

  const startDate = individual?.startDate || undefined;
  const payCalendarAssignedAt = individual?.payCalendarAssignedAt || undefined;

  const isPayCalendarDefaultSalary =
    payCalendar?.defaultAnnualGrossSalary && !individual?.annualGrossSalary;

  const paySchedule = derivePaySchedule(payCycles?.list);

  return (
    <>
      <ProfileSection title="Pay calendar details">
        <HStack>
          <ProfileField label="Pay calendar ID" value={payCalendar?.friendlyId} />
          <ProfileField
            label="Pay calendar name"
            value={payCalendar?.name}
            link={payCalendar && getPayCalendarProfilePath(payCalendar?.id, payCalendar.companyId)}
          />
        </HStack>
        <HStack>
          <ProfileField
            label="Start date"
            value={formatDate(startDate)}
            tooltip="If you don't set a specific start date, the employee will start accruing from the start date of the active pay cycle when it was created."
          />
          <ProfileField label="Salaried" value={individual?.salaried ? 'Yes' : 'No'} />
        </HStack>
        <ProfileField
          label="On this calendar since"
          value={formatDate(payCalendarAssignedAt)}
          tooltip="Older records may not carry this field yet."
        />
        {individual?.salaried && (
          <ProfileField
            label="Annual gross salary"
            value={`${formatValue({
              value:
                individual?.annualGrossSalary?.toString() ||
                payCalendar?.defaultAnnualGrossSalary?.toString(),
              prefix: '£',
              groupSeparator: ',',
              decimalSeparator: '.',
              decimalScale: 2,
            })}${isPayCalendarDefaultSalary ? ' (Pay calendar default)' : ''}`}
          />
        )}
        {!!individual?.salaryHistory?.length && (
          <TableContainer>
            <Text color="brand.black" textStyle="extraSmallRegular">
              Salary history
            </Text>
            <Table mt="20px">
              <Thead>
                <Tr>
                  <Th>Salary applies date</Th>
                  <Th>Salary</Th>
                </Tr>
              </Thead>
              <Tbody>
                {individual?.salaryHistory?.map((salary, index) => (
                  // Need to disable it because there is nothing unique -> https://github.com/jsx-eslint/eslint-plugin-react/blob/master/docs/rules/no-array-index-key.md#when-not-to-use-it
                  // eslint-disable-next-line react/no-array-index-key
                  <Tr key={index}>
                    <Td>{formatDate(salary.appliesDate) ?? '—'}</Td>
                    <Td>
                      {formatValue({
                        value: salary.annualGrossSalary.toString(),
                        prefix: '£',
                        groupSeparator: ',',
                        decimalSeparator: '.',
                        decimalScale: 2,
                      })}
                    </Td>
                  </Tr>
                ))}
              </Tbody>
            </Table>
          </TableContainer>
        )}
      </ProfileSection>
      <ProfileSection title="Pay schedule">
        <HStack>
          <ProfileField label="Last cut-off date" value={formatDate(paySchedule.lastCutoffDate)} />
          <ProfileField label="Last pay day" value={formatDate(paySchedule.lastPayday)} />
        </HStack>
        <HStack>
          <ProfileField label="Next cut-off date" value={formatDate(paySchedule.nextCutoffDate)} />
          <ProfileField label="Next pay day" value={formatDate(paySchedule.nextPayday)} />
        </HStack>
      </ProfileSection>
    </>
  );
}

export default IndividualPayrollTabView;
